/**
 * M&H Wishlist store.
 *
 * A tiny localStorage-backed favourites list. The backend is injected so a future
 * "sync to customer account metafield" project can swap it out without touching
 * any UI code:  new WishlistStore(new CustomerMetafieldBackend())  +  await wishlist.init()
 *
 * Every mutation dispatches a `wishlist:change` CustomEvent on `document`:
 *   detail = { action: 'add'|'remove'|'sync'|'init', id, count, items }
 *
 * Modelled on assets/recently-viewed-products.js.
 */

/** @typedef {{ id: string, handle: string, title: string, addedAt: number }} WishlistItem */

export const WISHLIST_CHANGED = 'wishlist:change';

/**
 * Backend contract (sync OR async):
 *   read()        -> WishlistItem[] | Promise<WishlistItem[]>
 *   write(items)  -> void | Promise<void>
 *   key           -> string   (used to spot our own cross-tab `storage` events)
 */
class LocalStorageBackend {
  static #KEY = 'mh:wishlist:v1';

  read() {
    try {
      return JSON.parse(localStorage.getItem(LocalStorageBackend.#KEY) || '[]');
    } catch (_) {
      return [];
    }
  }

  write(items) {
    try {
      localStorage.setItem(LocalStorageBackend.#KEY, JSON.stringify(items));
    } catch (_) {
      // Private mode / quota — in-memory state stays authoritative for the session.
    }
  }

  get key() {
    return LocalStorageBackend.#KEY;
  }
}

// Future drop-in — kept here as living documentation:
// export class CustomerMetafieldBackend {
//   async read() {
//     const res = await fetch('/apps/wishlist', { headers: { Accept: 'application/json' } });
//     return res.ok ? (await res.json()).items ?? [] : [];
//   }
//   async write(items) {
//     await fetch('/apps/wishlist', {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       body: JSON.stringify({ items }),
//     });
//   }
//   get key() { return 'mh:wishlist:sync'; }
// }

export class WishlistStore {
  #backend;
  /** @type {WishlistItem[]} */
  #items;

  constructor(backend = new LocalStorageBackend()) {
    this.#backend = backend;
    this.#items = this.#normalize(this.#backend.read());
  }

  /** For async backends — call once on boot. */
  async init() {
    this.#items = this.#normalize(await this.#backend.read());
    this.#emit('init');
  }

  /** @param {unknown} arr */
  #normalize(arr) {
    if (!Array.isArray(arr)) return [];
    const seen = new Set();
    return arr
      .filter((i) => i && i.id != null && !seen.has(String(i.id)) && seen.add(String(i.id)))
      .map((i) => ({
        id: String(i.id),
        handle: i.handle ?? '',
        title: i.title ?? '',
        addedAt: i.addedAt ?? Date.now(),
      }));
  }

  /** @returns {WishlistItem[]} newest first */
  getAll() {
    return this.#items.map((i) => ({ ...i }));
  }

  get count() {
    return this.#items.length;
  }

  /** @param {string|number} id */
  has(id) {
    const s = String(id);
    return this.#items.some((i) => i.id === s);
  }

  /** @param {{ id: string|number, handle?: string, title?: string }} item */
  add(item) {
    if (item?.id == null || this.has(item.id)) return false;
    this.#items = [
      { id: String(item.id), handle: item.handle ?? '', title: item.title ?? '', addedAt: Date.now() },
      ...this.#items,
    ];
    this.#commit('add', String(item.id));
    return true;
  }

  /** @param {string|number} id */
  remove(id) {
    const s = String(id);
    const next = this.#items.filter((i) => i.id !== s);
    if (next.length === this.#items.length) return false;
    this.#items = next;
    this.#commit('remove', s);
    return true;
  }

  /**
   * @param {{ id: string|number, handle?: string, title?: string }} item
   * @returns {boolean} whether the item is now in the wishlist
   */
  toggle(item) {
    if (this.has(item.id)) {
      this.remove(item.id);
      return false;
    }
    this.add(item);
    return true;
  }

  /** Re-read the backend without emitting (used by the cross-tab listener). */
  refresh() {
    this.#items = this.#normalize(this.#backend.read());
  }

  get storageKey() {
    return this.#backend.key;
  }

  #commit(action, id) {
    Promise.resolve(this.#backend.write(this.#items));
    this.#emit(action, id);
  }

  #emit(action, id = null) {
    document.dispatchEvent(
      new CustomEvent(WISHLIST_CHANGED, {
        detail: { action, id, count: this.#items.length, items: this.getAll() },
      })
    );
  }
}

/** Shared singleton. */
export const wishlist = new WishlistStore();

// Keep other tabs / windows in sync.
window.addEventListener('storage', (event) => {
  if (!event.key || event.key !== wishlist.storageKey) return;
  wishlist.refresh();
  document.dispatchEvent(
    new CustomEvent(WISHLIST_CHANGED, {
      detail: { action: 'sync', id: null, count: wishlist.count, items: wishlist.getAll() },
    })
  );
});
